import { z } from 'zod';

// Chat message validation
export const chatMessageSchema = z.object({
  message: z
    .string()
    .trim()
    .min(1, 'Message cannot be empty')
    .max(500, 'Message must be less than 500 characters')
    .refine(
      (val) => !/<script|javascript:|onerror=/i.test(val),
      'Invalid content detected'
    ),
});

// Tip validation
export const tipSchema = z.object({
  amount: z
    .number()
    .int('Amount must be a whole number')
    .min(10, 'Minimum tip is 10 coins')
    .max(100000, 'Maximum tip is 100,000 coins'),
  message: z
    .string()
    .trim()
    .max(200, 'Tip message must be less than 200 characters')
    .optional(),
});

// Wallet top-up validation
export const walletTopUpSchema = z.object({
  amount: z
    .number()
    .int('Amount must be a whole number')
    .min(100, 'Minimum purchase is ₦100'),
  coins: z
    .number()
    .int('Coins must be a whole number')
    .min(100, 'Minimum purchase is 100 coins'),
});

// Poll creation validation
export const pollSchema = z.object({
  question: z
    .string()
    .trim()
    .min(3, 'Question must be at least 3 characters')
    .max(200, 'Question must be less than 200 characters')
    .refine(
      (val) => !/<script|javascript:|onerror=/i.test(val),
      'Invalid content detected'
    ),
  options: z
    .array(
      z
        .string()
        .trim()
        .min(1, 'Option cannot be empty')
        .max(100, 'Option must be less than 100 characters')
    )
    .min(2, 'At least 2 options required')
    .max(10, 'Maximum 10 options allowed'),
  costPerVote: z
    .number()
    .int('Cost must be a whole number')
    .min(0, 'Cost cannot be negative')
    .max(1000, 'Maximum cost is 1000 coins'),
  duration: z
    .number()
    .int('Duration must be a whole number')
    .min(1, 'Minimum duration is 1 minute')
    .max(1440, 'Maximum duration is 24 hours'),
});

// Profile validation
export const profileSchema = z.object({
  displayName: z
    .string()
    .trim()
    .min(2, 'Display name must be at least 2 characters')
    .max(50, 'Display name must be less than 50 characters')
    .refine(
      (val) => /^[a-zA-Z0-9_\s-]+$/.test(val),
      'Display name can only contain letters, numbers, spaces, hyphens, and underscores'
    ),
  bio: z
    .string()
    .trim()
    .max(500, 'Bio must be less than 500 characters')
    .refine(
      (val) => !/<script|javascript:|onerror=/i.test(val),
      'Invalid content detected'
    )
    .optional(),
});

// Poll vote validation
export const voteSchema = z.object({
  pollId: z.string().uuid('Invalid poll ID'),
  optionIndex: z.number().int().min(0, 'Invalid option'),
  cost: z.number().int().min(0, 'Invalid cost'),
});

// Transaction metadata validation
export const transactionMetadataSchema = z.object({
  payment_method: z.enum(['flutterwave', 'wallet']).optional(),
  stream_id: z.string().uuid().optional(),
  to_user_id: z.string().uuid().optional(),
  from_user_id: z.string().uuid().optional(),
  tip_message_id: z.string().uuid().optional(),
  poll_id: z.string().uuid().optional(),
  vote_count: z.number().int().positive().optional(),
  reason: z.string().max(100).optional(),
  admin_id: z.string().uuid().optional(),
  recovery: z.boolean().optional(),
  error: z.string().max(200).optional(),
  original_reference: z.string().max(100).optional(),
  notes: z.string().max(200).optional(),
  admin_action: z.boolean().optional(),
}).strict();
